import { type ReactNode } from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import { Banner } from '@depop/web-ui-kit/Banner';
import { CheckmarkIcon } from '@depop/web-ui-kit/Icons/CheckmarkIcon';
import { InfoIcon } from '@depop/web-ui-kit/Icons/InfoIcon';
import { WarningIconFilled } from '@depop/web-ui-kit/Icons/WarningIconFilled';
import clsx from 'clsx';
import DOMPurify from 'dompurify';

import styles from './styles.module.css';

import {
  type InlineBanner,
  type InlineBanners,
} from '@/modules/banners/aliases';

const SELLER_BUNDLE_DISCOUNT = 'seller_bundle_discount';

const BUNDLE_COMPLETE = 'COMPLETE';

const STYLES = {
  SUCCESS: { type: 'success', icon: <CheckmarkIcon /> },
  DANGER: { type: 'error', icon: <WarningIconFilled /> },
  NEUTRAL: { type: 'information', icon: <InfoIcon /> },
} as const satisfies Record<
  string,
  { type: 'success' | 'error' | 'information'; icon: ReactNode }
>;

const FALLBACK_STYLE = STYLES.NEUTRAL;

type Props = {
  banners?: InlineBanners;
  className?: string;
};

export function InlineCampaignBanners({ banners, className }: Props) {
  const visibleBanners =
    banners?.filter((banner: InlineBanner) => {
      if (banner.type !== SELLER_BUNDLE_DISCOUNT) {
        return true;
      }

      return banner.details?.type === BUNDLE_COMPLETE;
    }) ?? [];

  if (visibleBanners.length === 0) {
    return null;
  }

  return (
    <div className={clsx(styles.container, className)}>
      {visibleBanners.map((banner, index) => {
        const { type, icon } =
          STYLES[banner.style as keyof typeof STYLES] ?? FALLBACK_STYLE;

        return (
          <Banner
            key={`${banner.type}-${index}`}
            type={type}
            iconLeft={icon}
            purpose="toast"
            className={styles.banner}
          >
            <Caption variant="lg-regular" className={styles.bannerText}>
              <span
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(banner.text),
                }}
              />
            </Caption>
          </Banner>
        );
      })}
    </div>
  );
}
